<template>
  <v-dialog v-model="dialog" width="400">
    <AddEditGroupsDialog
      ref="addEditGroupInDialog"
      @changeEvent="doneNewGroup"
    />
    <v-card>
      <v-card-title class="headline"> Assign playlist </v-card-title>

      <v-card-text>
        <assign-dropdown
          :value="selectedUsers"
          :set-drop-down-value="setDropdownValue"
        ></assign-dropdown>
        <v-autocomplete
          v-model="groupSelect"
          :items="groups"
          item-text="name"
          item-value="id"
          :menu-props="{ closeOnClick: true }"
          label="Search groups"
          multiple
          append-icon=""
          prepend-icon="fal fa-user-friends"
          style="margin-top: 20px"
        >
          <template v-slot:selection="{ item }">
            <v-chip :small="groupSelect.length >= 4">
              <span>{{ item.name }}</span>
            </v-chip>
          </template>

          <template v-if="canUpdateCreateGroup" v-slot:prepend-item>
            <v-list-item ripple @click="createNewGroup()">
              <v-list-item-content>
                <v-list-item-title
                  style="
                    font-size: 17px;
                    display: flex;
                    justify-content: center;
                    gap: 7px;
                    align-items: center;
                  "
                >
                  <i class="fas fa-plus"></i>
                  <span>Create New Group</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>

          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
              <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-row no-gutters align="center">
                  <span>{{ item.name }}</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="canUpdateCreateGroup"
                    icon
                    large
                    v-on:click.stop="editGroup(item)"
                  >
                    <v-icon medium>fal fa-edit</v-icon>
                  </v-btn>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
        <v-menu
          ref="dueDateMenu"
          v-model="dueDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dueDate"
              label="Due Date"
              prepend-icon="fal fa-calendar"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="date" no-title scrollable>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="dueDateMenu = false">
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.dueDateMenu.save(dueDate)"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="toggleAssignDialog"> Close </v-btn>
        <v-btn
          color="primary"
          text
          @click="assign"
          :loading="assignLoader"
          :disabled="assignLoader"
        >
          Assign
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-overlay :value="assignLoader">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import {
  findIndex as _findIndex,
  each as _each,
  isEmpty as _isEmpty,
  debounce as _debounce,
  map as _map,
  some as _some,
} from 'lodash-es';
import dayjs from 'dayjs';
import AssignDropdown from '../client-lms/AssignDropdown';
import AddEditGroupsDialog from '@/components/user-management/user/partial/AddNewGroupDialog.vue';
import { canUpdateCreateGroups } from './../../helper/user/roleHelper';

export default {
  name: 'AssignPlaylist',
  components: { AssignDropdown, AddEditGroupsDialog },
  props: {
    dialog: Boolean,
    toggleAssignDialog: Function,
    playlist: Object,
  },
  data() {
    return {
      date: new Date().toISOString().substr(0, 10),
      groupSelect: [],
      groups: [],
      assignToAll: false,
      loading: true,
      dueDate: this.formatDate(new Date().toISOString().substr(0, 10)),
      dueDateMenu: false,
      selectedUsers: [],
      assignLoader: false,
      usersSearchResult: [],
      searchUsers: null,
      selectLoading: {
        users: true,
      },
    };
  },
  computed: {
    canUpdateCreateGroup() {
      return canUpdateCreateGroups(this.$auth.user().roles);
    },
    dateFormatted() {
      if (this.dueDate === null) {
        return '';
      }

      return dayjs(this.dueDate).format('MM/DD/YYYY');
    },
    showSelectedUsers() {
      return this.selectedUsers.length > 0;
    },
  },
  created() {
    this.getGroups();
  },
  methods: {
    getFirstTenUsers() {
      this.selectLoading.users = true;
      this.$http
        .get('get-random-users', {
          params: {
            users: 10,
          },
        })
        .then(({ data }) => {
          _each(data, (user) => {
            const index = _findIndex(this.usersSearchResult, { id: user.id });
            if (index === -1) {
              this.usersSearchResult.push(user);
            }
          });
          this.selectLoading.users = false;
        })
        .catch((error) => {
          this.selectLoading.users = false;
        });
    },
    getGroups() {
      this.$http
        .get('/groups')
        .then((response) => {
          _each(response.data, (group) => {
            this.groups.push(group);
          });
        })
        .catch((err) => {});
    },
    searchUser: _debounce(function (user) {
      this.fetchUsers(user);
    }, 500),
    fetchUsers(filter) {
      const params = {};
      if (filter) {
        params.filter = filter;
      }

      this.selectLoading.users = true;

      this.$http
        .get('/playlist/assign', { params })
        .then(({ data }) => {
          _each(data, (user) => {
            const index = _findIndex(this.usersSearchResult, { id: user.id });
            if (index === -1) {
              this.usersSearchResult.push(user);
            }
          });

          this.selectLoading.users = false;
        })
        .catch(() => {
          this.selectLoading.users = false;
        });
    },
    assign() {
      this.assignLoader = true;
      const selectedUsers = _map(this.selectedUsers, (user) => {
        return user.id;
      });
      const users = this.assignToAll ? 'all' : selectedUsers;
      const groups = _isEmpty(this.groupSelect) ? [] : this.groupSelect;
      if (users.length < 1 && groups.length < 1) {
        this.assignLoader = false;
        this.$swal.fire({
          icon: 'error',
          title: 'Problem',
          text: 'No User/Group selected',
        });
      } else {
        this.$http
          .post('/playlist/assign', {
            playlist_id: this.playlist.id,
            users,
            groups,
            due_date: this.dueDate,
          })
          .then(() => {
            this.$swal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Playlist successfully assigned',
            });
            this.toggleAssignDialog();
            // reset to default
            this.selectedUsers = [];
            this.groupSelect = [];
            this.assignToAll = false;
            this.dueDate = new Date().toISOString().substr(0, 10);
            this.value = false;
            this.assignLoader = false;
          })
          .catch(() => {
            this.assignLoader = false;
            this.toggleAssignDialog();
            this.$swal.fire({
              icon: 'error',
              title: 'Problem',
              text: 'Error occured',
            });
            this.value = false;
          });
      }
    },
    selectedUsersChanged() {
      this.searchUsers = '';
    },
    setDropdownValue(value) {
      this.assignToAll = value == 'Selected all' ? true : false;
      this.selectedUsers = value;
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },
    editGroup(group) {
      this.hideMenuContentWhenOpeningNewDialog();
      this.$refs['addEditGroupInDialog'].toggleDialog('Edit Group', group);
    },
    hideMenuContentWhenOpeningNewDialog() {
      let el = document.getElementsByClassName('menuable__content__active');
      el[0].classList.add('display-none');
    },
    createNewGroup() {
      this.hideMenuContentWhenOpeningNewDialog();
      this.$refs['addEditGroupInDialog'].toggleDialog(
        'Create New Group',
        false
      );
    },
    doneNewGroup(item) {
      if (item.isUpdate) {
        let indexToModify = this.groups.findIndex(
          (group) => group.id === item.id
        );
        if (indexToModify > -1) {
          this.groups[indexToModify].name = item.name;
          this.groups[indexToModify].users = item.users;
        }

        if (!this.groupSelect.includes(item.id)) {
          this.groupSelect.push(item.id);
        }
      } else {
        if (item) this.groups.push(item);
        if (item.id) this.groupSelect.push(item.id);
      }
    },
  },
  watch: {
    searchUsers(val) {
      if (val) {
        this.fetchUsers(val);
      }
    },
    date(val) {
      this.dueDate = dayjs(this.date).format('MM/DD/YYYY');
    },
  },
};
</script>

<style scoped></style>
